import { calculateScreenLineDirections, cameraPoseChanged, clonePreservingType, DetectionZone, Device, deviceToFacility, Facility, facilityToDevice, FacilityTargets, ILaneLine, LaneLine, reprojectShapePoints, ScreenLine, selectAnchoredShapes, Vector2 } from "@app/shared";
import { useCallback, useMemo, useRef, useState } from "react";
import { toastWarning } from "../../../../helpers";
import { ShapeManagerState } from "./useShapeManager";

export interface DeviceAlignmentPose {
    x: number;
    y: number;
    headingDegrees: number;
}

type AlignShape = DetectionZone | ScreenLine | LaneLine;

const TRACK_COLOR = "#3b82f6";
const GHOST_COLOR = "#9ca3af";

const TRACKS_LOOKBACK_MINUTES = 60;
const TRACKS_MAX_TARGETS = 150;
const TRACKS_MAX_POINTS = 40;

const normalizeHeading = (degrees: number) => ((degrees % 360) + 360) % 360;

/** Smallest signed angle from `from` to `to` in degrees (-180..180]. */
const headingDelta = (from: number, to: number) => ((to - from + 540) % 360) - 180;

/** The shapes that ride with a device when it moves: its zones/screen lines, and lane lines it owns (or all facility lane lines at single-device sites). */
const selectRigShapes = (allShapes: { $type?: string }[], forDevice: Device, isOnlyDevice: boolean): AlignShape[] =>
    selectAnchoredShapes(allShapes, forDevice, isOnlyDevice) as AlignShape[];

/** Rebuild a shape on new facility-frame points, fixing up the metadata that follows the heading. */
const withPoseFixups = (shape: AlignShape, points: Vector2[], baselineHeadingDegrees: number, newHeadingDegrees: number): AlignShape => {
    const clone = clonePreservingType(shape, { points });
    if (clone.$type === ScreenLine.$type && points.length >= 2) {
        const line = clone as ScreenLine;
        const directions = calculateScreenLineDirections(points[0], points[1], line.reverseInOut);
        line.inboundDirection = directions.inbound;
        line.outboundDirection = directions.outbound;
    } else if (clone.$type === DetectionZone.$type) {
        const zone = clone as DetectionZone;
        if (zone.zoneCardinalDirectionDegrees != null) {
            // The cardinal direction is a facility-frame compass heading — rotate it with the rig.
            zone.zoneCardinalDirectionDegrees = Math.round(normalizeHeading(zone.zoneCardinalDirectionDegrees + headingDelta(baselineHeadingDegrees, newHeadingDegrees)));
        }
    }
    return clone;
};

/** Rigid translate+rotate of a rig shape. Correct for position/heading only — see applyPose. */
const projectShapeThroughPose = (shape: AlignShape, dPoints: Vector2[], pose: DeviceAlignmentPose, baselineHeadingDegrees: number): AlignShape =>
    withPoseFixups(shape, deviceToFacility(dPoints, { x: pose.x, y: pose.y }, pose.headingDegrees), baselineHeadingDegrees, pose.headingDegrees);

export interface DeviceAlignmentState {
    active: boolean;
    device: Device | undefined;
    baselinePose: DeviceAlignmentPose | null;
    candidatePose: DeviceAlignmentPose | null;
    /** Ids of the shapes that move with the device (its lane lines + device-anchored zones/screen lines) */
    rigShapeIds: Set<string>;
    /** Rig shapes re-projected at the candidate pose (facility meters) */
    alignedRigShapes: AlignShape[];
    /** The device rendered at the candidate pose */
    alignedDevice: Device | null;
    /** Baseline lane lines, grayed out, so the original position stays visible while dragging */
    ghostLaneLines: LaneLine[];
    /** Recent target tracks re-projected at the candidate pose, as lane-line polylines */
    trackLines: LaneLine[];
    tracksLoading: boolean;
    trackCount: number;
    rigShapeCount: number;
    /**
     * Whether the rig moves with the device. Off, only the device pose changes: its lane lines,
     * zones and screen lines stay where they are on the map while recent tracks still follow the
     * device — for correcting a device whose geometry is already drawn in the right place.
     */
    moveGeometry: boolean;
    setMoveGeometry: (move: boolean) => void;
    offset: { east: number; south: number; headingDelta: number } | null;
    hasMoved: boolean;
    enter: (device: Device) => void;
    cancel: () => void;
    /** Apply the candidate pose: writes the device + re-projected rig shapes into the shape manager and exits */
    commit: () => void;
    /**
     * One-shot pose application (reference-point alignment): move the device and its rig from
     * its current pose to `pose` and merge `extraDeviceFields` (height/pitch/roll) onto the
     * device — same rig semantics as interactive align + commit, without entering the mode.
     */
    applyPose: (forDevice: Device, pose: DeviceAlignmentPose, extraDeviceFields?: object) => void;
    resetPose: () => void;
    nudge: (eastMeters: number, southMeters: number, headingDeltaDegrees: number) => void;
    setCandidatePosition: (x: number, y: number) => void;
    setCandidateHeading: (headingDegrees: number) => void;
    refreshTracks: () => void;
}

interface UseDeviceAlignmentProps {
    facility: Facility;
    shapeManager: ShapeManagerState;
}

/**
 * "Align device" mode: hold the device's world (its lane lines, device-anchored shapes, and
 * recent radar tracks) rigid while the user slides/rotates it over the map imagery. The rig is
 * snapshotted in device-frame coordinates on entry and re-projected live through a candidate
 * pose; map-anchored shapes never move. Committing rewrites the device pose and rig geometry
 * in the shape manager — the regular Save persists it (and nudges the device to re-sync).
 *
 * `moveGeometry` (on by default) can be switched off to hold the rig in place as well: the device
 * and its tracks still follow the candidate pose, but Done then rewrites only the device.
 */
export function useDeviceAlignment({ facility, shapeManager }: UseDeviceAlignmentProps): DeviceAlignmentState {
    const [active, setActive] = useState(false);
    const [device, setDevice] = useState<Device>();
    const [baselinePose, setBaselinePose] = useState<DeviceAlignmentPose | null>(null);
    const [candidatePose, setCandidatePose] = useState<DeviceAlignmentPose | null>(null);
    const [baselineShapes, setBaselineShapes] = useState<AlignShape[]>([]);
    const [deviceFrameShapes, setDeviceFrameShapes] = useState<Map<string, Vector2[]>>(new Map());
    const [deviceFrameTracks, setDeviceFrameTracks] = useState<{ id: string; points: Vector2[] }[]>([]);
    const [tracksLoading, setTracksLoading] = useState(false);
    const [moveGeometry, setMoveGeometry] = useState(true);

    // Session token so a slow tracks fetch can't write into a later alignment session.
    const sessionRef = useRef(0);

    // With the rig held in place nothing is taken over from the regular map layer: the shapes keep
    // rendering where they are and only the device (and its tracks) follow the candidate pose.
    const rigShapeIds = useMemo(() => new Set(moveGeometry ? baselineShapes.map((s) => s.id!).filter(Boolean) : []), [baselineShapes, moveGeometry]);

    const loadTracks = useCallback(
        async (forDevice: Device, pose: DeviceAlignmentPose) => {
            const session = sessionRef.current;
            setTracksLoading(true);
            try {
                const tracks = (await FacilityTargets.getRecentTracks(facility.id, forDevice.id, TRACKS_LOOKBACK_MINUTES, TRACKS_MAX_TARGETS, TRACKS_MAX_POINTS)) ?? [];
                const devicePosition = { x: pose.x, y: pose.y };
                const converted = tracks
                    .map((track) => ({
                        id: track.targetId ?? "",
                        points: (track.points ?? [])
                            .map((p) => {
                                // Prefer the true device-frame position reported by the radar; fall back to
                                // inverting the stored facility position with the pose used to project it.
                                if (p.rawPosition) return new Vector2({ x: p.rawPosition.x, y: p.rawPosition.y });
                                if (p.position) return facilityToDevice([new Vector2({ x: p.position.x, y: p.position.y })], devicePosition, pose.headingDegrees)[0];
                                return undefined;
                            })
                            .filter((p): p is Vector2 => !!p),
                    }))
                    .filter((track) => track.id && track.points.length >= 2);
                if (sessionRef.current === session) {
                    setDeviceFrameTracks(converted);
                }
            } catch (err) {
                console.error("Failed to load recent tracks for alignment", err);
                if (sessionRef.current === session) {
                    setDeviceFrameTracks([]);
                }
            } finally {
                if (sessionRef.current === session) {
                    setTracksLoading(false);
                }
            }
        },
        [facility.id],
    );

    const enter = useCallback(
        (forDevice: Device) => {
            const pose: DeviceAlignmentPose = {
                x: forDevice.positionX ?? 0,
                y: forDevice.positionY ?? 0,
                headingDegrees: normalizeHeading(forDevice.headingDegrees ?? 0),
            };

            // Facility-scoped lane lines belong to the only device at single-device sites;
            // at multi-device sites they're ambiguous, so they stay map-anchored.
            const isOnlyDevice = (facility.devices?.length ?? 0) <= 1;
            const rig = selectRigShapes(shapeManager.allShapes, forDevice, isOnlyDevice);

            const devicePosition = { x: pose.x, y: pose.y };
            const dFrame = new Map<string, Vector2[]>();
            rig.forEach((s) => {
                if (s.id) dFrame.set(s.id, facilityToDevice(s.points ?? [], devicePosition, pose.headingDegrees));
            });

            sessionRef.current += 1;
            setDevice(forDevice);
            setBaselinePose(pose);
            setCandidatePose(pose);
            setBaselineShapes(rig);
            setDeviceFrameShapes(dFrame);
            setDeviceFrameTracks([]);
            setMoveGeometry(true);
            setActive(true);
            void loadTracks(forDevice, pose);
        },
        [facility.devices, shapeManager, loadTracks],
    );

    const exit = useCallback(() => {
        sessionRef.current += 1;
        setActive(false);
        setDevice(undefined);
        setBaselinePose(null);
        setCandidatePose(null);
        setBaselineShapes([]);
        setDeviceFrameShapes(new Map());
        setDeviceFrameTracks([]);
        setTracksLoading(false);
    }, []);

    const projectRigShape = useCallback(
        (shape: AlignShape, pose: DeviceAlignmentPose, baseline: DeviceAlignmentPose): AlignShape => {
            const dPoints = (shape.id && deviceFrameShapes.get(shape.id)) || [];
            return projectShapeThroughPose(shape, dPoints, pose, baseline.headingDegrees);
        },
        [deviceFrameShapes],
    );

    const alignedRigShapes = useMemo(() => {
        if (!active || !moveGeometry || !candidatePose || !baselinePose) return [];
        return baselineShapes.map((s) => projectRigShape(s, candidatePose, baselinePose));
    }, [active, moveGeometry, candidatePose, baselinePose, baselineShapes, projectRigShape]);

    const alignedDevice = useMemo(() => {
        if (!active || !device || !candidatePose) return null;
        return clonePreservingType(device, { positionX: candidatePose.x, positionY: candidatePose.y, headingDegrees: candidatePose.headingDegrees });
    }, [active, device, candidatePose]);

    const ghostLaneLines = useMemo(() => {
        if (!active || !moveGeometry) return [];
        return baselineShapes.filter((s) => s.$type === LaneLine.$type).map((s) => clonePreservingType(s as LaneLine, { id: `align-ghost-${s.id}`, color: GHOST_COLOR, name: undefined }));
    }, [active, moveGeometry, baselineShapes]);

    const trackLines = useMemo(() => {
        if (!active || !candidatePose) return [];
        const devicePosition = { x: candidatePose.x, y: candidatePose.y };
        return deviceFrameTracks.map((track) => {
            const line = new LaneLine({ id: `align-track-${track.id}`, color: TRACK_COLOR } as ILaneLine);
            line.points = deviceToFacility(track.points, devicePosition, candidatePose.headingDegrees);
            return line;
        });
    }, [active, candidatePose, deviceFrameTracks]);

    const offset = useMemo(() => {
        if (!active || !baselinePose || !candidatePose) return null;
        return {
            east: candidatePose.x - baselinePose.x,
            south: candidatePose.y - baselinePose.y,
            headingDelta: headingDelta(baselinePose.headingDegrees, candidatePose.headingDegrees),
        };
    }, [active, baselinePose, candidatePose]);

    const hasMoved = !!offset && (Math.abs(offset.east) >= 0.05 || Math.abs(offset.south) >= 0.05 || Math.abs(offset.headingDelta) >= 0.05);

    const commit = useCallback(() => {
        if (!active || !device || !candidatePose || !baselinePose) return;
        if (moveGeometry) {
            baselineShapes.forEach((s) => {
                const projected = projectRigShape(s, candidatePose, baselinePose);
                // A facility-scoped lane line that moved with this device's rig is anchored to it from now on.
                if (projected.$type === LaneLine.$type && !(projected as LaneLine).deviceId) {
                    (projected as LaneLine).deviceId = device.id;
                }
                shapeManager.updateShape(projected);
            });
        }
        shapeManager.updateShape(clonePreservingType(device, { positionX: candidatePose.x, positionY: candidatePose.y, headingDegrees: candidatePose.headingDegrees }));
        exit();
    }, [active, device, candidatePose, baselinePose, baselineShapes, moveGeometry, shapeManager, projectRigShape, exit]);

    const applyPose = useCallback(
        (forDevice: Device, pose: DeviceAlignmentPose, extraDeviceFields?: object) => {
            const baseline: DeviceAlignmentPose = {
                x: forDevice.positionX ?? 0,
                y: forDevice.positionY ?? 0,
                headingDegrees: normalizeHeading(forDevice.headingDegrees ?? 0),
            };
            const isOnlyDevice = (facility.devices?.length ?? 0) <= 1;
            const rig = selectRigShapes(shapeManager.allShapes, forDevice, isOnlyDevice);
            const devicePosition = { x: baseline.x, y: baseline.y };
            const nextDevice = clonePreservingType(forDevice, { positionX: pose.x, positionY: pose.y, headingDegrees: normalizeHeading(pose.headingDegrees), ...extraDeviceFields });

            // A reference-point fit changes height/pitch/roll as well as the placement. The rigid
            // translate+rotate below carries position and heading, but nothing about it corrects for
            // a changed camera geometry — so re-derive through the image whenever those terms moved,
            // keeping each shape on the pixels it was drawn over. Falls back to the rigid transform
            // for shapes with a point at/past the horizon, which cannot survive the round trip.
            const reprojectCamera = cameraPoseChanged(forDevice, nextDevice);
            const stale: string[] = [];

            rig.forEach((s) => {
                if (reprojectCamera) {
                    const points = reprojectShapePoints(forDevice, nextDevice, s.points ?? []);
                    if (points) {
                        const reprojected = withPoseFixups(s, points, baseline.headingDegrees, pose.headingDegrees);
                        if (reprojected.$type === LaneLine.$type && !(reprojected as LaneLine).deviceId) {
                            (reprojected as LaneLine).deviceId = forDevice.id;
                        }
                        shapeManager.updateShape(reprojected);
                        return;
                    }
                    stale.push(s.name || "a shape");
                }

                const dPoints = facilityToDevice(s.points ?? [], devicePosition, baseline.headingDegrees);
                const projected = projectShapeThroughPose(s, dPoints, pose, baseline.headingDegrees);
                // A facility-scoped lane line that moved with this device's rig is anchored to it from now on.
                if (projected.$type === LaneLine.$type && !(projected as LaneLine).deviceId) {
                    (projected as LaneLine).deviceId = forDevice.id;
                }
                shapeManager.updateShape(projected);
            });

            shapeManager.updateShape(nextDevice);
            if (stale.length > 0) {
                toastWarning("Some shapes could not be re-aligned", `${stale.join(", ")} ${stale.length === 1 ? "has a point" : "have points"} at or past the horizon, so ${stale.length === 1 ? "it was" : "they were"} only moved with the device. Check ${stale.length === 1 ? "it" : "them"} against the camera view.`, 10000);
            }
        },
        [facility.devices, shapeManager],
    );

    const resetPose = useCallback(() => {
        setCandidatePose((current) => (baselinePose ? { ...baselinePose } : current));
    }, [baselinePose]);

    const nudge = useCallback((eastMeters: number, southMeters: number, headingDeltaDegrees: number) => {
        setCandidatePose((current) => (current ? { x: current.x + eastMeters, y: current.y + southMeters, headingDegrees: normalizeHeading(current.headingDegrees + headingDeltaDegrees) } : current));
    }, []);

    const setCandidatePosition = useCallback((x: number, y: number) => {
        setCandidatePose((current) => (current ? { ...current, x, y } : current));
    }, []);

    const setCandidateHeading = useCallback((degrees: number) => {
        setCandidatePose((current) => (current ? { ...current, headingDegrees: normalizeHeading(degrees) } : current));
    }, []);

    const refreshTracks = useCallback(() => {
        if (!device || !baselinePose) return;
        void loadTracks(device, baselinePose);
    }, [device, baselinePose, loadTracks]);

    return {
        active,
        device,
        baselinePose,
        candidatePose,
        rigShapeIds,
        alignedRigShapes,
        alignedDevice,
        ghostLaneLines,
        trackLines,
        tracksLoading,
        trackCount: deviceFrameTracks.length,
        rigShapeCount: baselineShapes.length,
        moveGeometry,
        setMoveGeometry,
        offset,
        hasMoved,
        enter,
        cancel: exit,
        commit,
        applyPose,
        resetPose,
        nudge,
        setCandidatePosition,
        setCandidateHeading,
        refreshTracks,
    };
}
